// File: frontend/src/pages/Socrates/components/Dashboard/index.js

import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGlobe, faUsers, 
  faCog, faPlus, faArrowRight,
  faExclamationCircle, faSync
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useTemplate } from '../../contexts/TemplateContext';
import { useSocrates } from '../../contexts/SocratesContext';
import OverviewCards from './OverviewCards';
import QuickActions from './QuickActions';
import SocratesCard from '../common/SocratesCard';
import LoadingSpinner from '../common/LoadingSpinner';
import styles from './Dashboard.module.css';

const Dashboard = ({ onNavigate }) => {
  const { t } = useLanguage();
  const { 
    templates, 
    systemTemplates, 
    userTemplates, 
    templatesLoaded,
    hasInitialized,
    initializeTemplates,
    refreshTemplates,
    openCreateForm 
  } = useTemplate();
  const { loading, error, setError } = useSocrates();
  
  const [dashboardStats, setDashboardStats] = useState({
    totalTemplates: 0,
    systemTemplates: 0,
    userTemplates: 0,
    activeTemplates: 0
  });

  // Use ref to track if we've already initialized to prevent double initialization in Strict Mode
  const hasInitializedRef = useRef(false);

  // Initialize templates on mount - FIXED: use hasInitialized flag
  useEffect(() => {
    console.log('Dashboard useEffect - hasInitialized:', hasInitialized, 'hasInitializedRef:', hasInitializedRef.current);
    
    if (!hasInitialized && !hasInitializedRef.current) {
      console.log('Initializing templates from Dashboard...');
      hasInitializedRef.current = true;
      initializeTemplates();
    }
  }, [hasInitialized, initializeTemplates]);

  // Reset ref when component unmounts
  useEffect(() => {
    return () => {
      hasInitializedRef.current = false;
    };
  }, []);

  // Calculate dashboard statistics when templates change
  useEffect(() => {
    const totalTemplates = templates.length;
    const systemCount = systemTemplates.length;
    const userCount = userTemplates.length;
    const activeCount = templates.filter(t => t.is_active !== 'false').length;

    setDashboardStats({
      totalTemplates,
      systemTemplates: systemCount,
      userTemplates: userCount,
      activeTemplates: activeCount
    });
  }, [templates, systemTemplates, userTemplates, t]);

  // Refresh dashboard data
  const handleRefresh = async () => {
    try {
      setError(null);
      hasInitializedRef.current = false; // Reset ref to allow re-initialization
      await refreshTemplates();
    } catch (error) {
      setError(error.message || t('failed_to_refresh_dashboard'));
    }
  };

  // Show loading spinner only during initial load
  if (!hasInitialized && loading) {
    return <LoadingSpinner size="large" message={t('loading_dashboard')} />;
  }

  return (
    <div className={styles.dashboard}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>{t('socrates_dashboard')}</h1>
          <p className={styles.subtitle}>{t('socrates_dashboard_description')}</p>
        </div>
        <div className={styles.headerRight}>
          <button 
            className={styles.refreshButton}
            onClick={handleRefresh}
            disabled={loading}
            title={t('refresh_dashboard')}
          >
            <FontAwesomeIcon icon={faSync} spin={loading} />
            <span>{t('refresh')}</span>
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className={styles.errorBanner}>
          <FontAwesomeIcon icon={faExclamationCircle} className={styles.errorIcon} />
          <span>{error}</span>
          <button 
            className={styles.dismissError}
            onClick={() => setError(null)}
          >
            ×
          </button>
        </div>
      )}

      {/* Overview Cards */}
      <OverviewCards stats={dashboardStats} onNavigate={onNavigate} />

      {/* Quick Actions */}
      <QuickActions onNavigate={onNavigate} onCreateTemplate={openCreateForm} />

      {/* Main Content */}
      <div className={styles.contentGrid}>
        {/* Recent Templates */}
        <SocratesCard className={styles.recentTemplatesCard}>
          <div className={styles.cardHeader}>
            <h2 className={styles.cardTitle}>
              <FontAwesomeIcon icon={faCog} className={styles.cardIcon} />
              {t('recent_templates')}
            </h2>
            <button 
              className={styles.viewAllButton}
              onClick={() => onNavigate('templates')}
            >
              {t('view_all')}
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>
          
          <div className={styles.cardContent}>
            {templates.length === 0 ? (
              <div className={styles.emptyState}>
                <FontAwesomeIcon icon={faCog} className={styles.emptyIcon} />
                <p className={styles.emptyText}>
                  {hasInitialized ? t('no_templates_yet') : t('loading_templates')}
                </p>
                {hasInitialized && (
                  <button 
                    className={styles.createFirstButton}
                    onClick={openCreateForm}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    {t('create_first_template')}
                  </button>
                )}
              </div>
            ) : (
              <div className={styles.templatesList}>
                {templates.slice(0, 5).map((template) => (
                  <div key={template.template_id} className={styles.templateItem}>
                    <div className={styles.templateInfo}>
                      <div className={styles.templateHeader}>
                        <FontAwesomeIcon 
                          icon={template.is_system_template === 'true' ? faGlobe : faUsers} 
                          className={`${styles.templateTypeIcon} ${
                            template.is_system_template === 'true' ? styles.system : styles.user
                          }`}
                        />
                        <h3 className={styles.templateName}>{template.template_name}</h3>
                      </div>
                      <p className={styles.templateDescription}>
                        {template.template_description || t('no_description')}
                      </p>
                      <div className={styles.templateMeta}>
                        <span className={styles.parameterCount}>
                          {template.parameters?.length || 0} {t('parameters')}
                        </span>
                        <span className={styles.templateStatus}>
                          {template.is_active !== 'false' ? t('active') : t('inactive')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </SocratesCard>
      </div>
    </div>
  );
};

export default Dashboard;